/**
 * Serac icon set — line icons at 1.4–1.75 stroke weight.
 *
 * Don't import an external icon library. If a needed glyph is missing,
 * hand-author it inline in the same line-weight style and add it here.
 */
import type { SVGProps } from 'react';

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  s?: number;
}

const base = ({ s = 14, ...rest }: IconProps) => ({
  width: s,
  height: s,
  viewBox: '0 0 24 24',
  ...rest,
});

export const Icon = {
  arrow: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  play: (p: IconProps = {}) => (
    <svg {...base(p)} fill="currentColor">
      <path d="M7 5v14l12-7z" />
    </svg>
  ),
  pause: (p: IconProps = {}) => (
    <svg {...base(p)} fill="currentColor">
      <rect x="6" y="5" width="4" height="14" />
      <rect x="14" y="5" width="4" height="14" />
    </svg>
  ),
  check: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.5l5 5 9-11" />
    </svg>
  ),
  x: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  ),
  edit: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20h4L19 9l-4-4L4 16v4z" />
      <path d="M13.5 6.5l4 4" />
    </svg>
  ),
  plus: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  search: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  ),
  chev: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <path d="M9 6l6 6-6 6" />
    </svg>
  ),
  chevDown: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  github: (p: IconProps = {}) => (
    <svg {...base(p)} fill="currentColor">
      <path d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.89 1.53 2.34 1.09 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.56-1.11-4.56-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.65 0 0 .84-.27 2.75 1.02a9.5 9.5 0 0 1 5 0c1.91-1.29 2.75-1.02 2.75-1.02.55 1.38.2 2.4.1 2.65.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.69-4.57 4.93.36.31.68.92.68 1.85v2.74c0 .27.18.58.69.48A10 10 0 0 0 12 2z" />
    </svg>
  ),
  terminal: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 9l3 3-3 3M13 15h4" />
    </svg>
  ),
  key: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <circle cx="8" cy="15" r="4" />
      <path d="M11 12l9-9M16 7l3 3" />
    </svg>
  ),
  layers: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <path d="M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5M3 18l9 5 9-5" />
    </svg>
  ),
  box: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <path d="M3 7l9-4 9 4v10l-9 4-9-4V7z" />
      <path d="M3 7l9 4 9-4M12 11v10" />
    </svg>
  ),
  book: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <path d="M4 4h11a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3V4z" />
      <path d="M4 17a3 3 0 0 1 3-3h11" />
    </svg>
  ),
  run: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <path d="M5 4l14 8-14 8V4z" />
    </svg>
  ),
  settings: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.82-.33 1.7 1.7 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .33-1.82 1.7 1.7 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09c.02.68.4 1.29 1 1.51a1.7 1.7 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.22.6.83.98 1.51 1H21a2 2 0 1 1 0 4h-.09c-.68.02-1.29.4-1.51 1z" />
    </svg>
  ),
  activity: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12h4l3-9 4 18 3-9h4" />
    </svg>
  ),
  clock: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  folder: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
    </svg>
  ),
  external: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 4h6v6M10 14L20 4M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
    </svg>
  ),
  shield: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <path d="M12 3l8 3v6c0 4.5-3.3 8.5-8 9-4.7-.5-8-4.5-8-9V6l8-3z" />
    </svg>
  ),
  bolt: (p: IconProps = {}) => (
    <svg {...base(p)} fill="currentColor">
      <path d="M13 2L3 14h7l-1 8 10-12h-7z" />
    </svg>
  ),
  git: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75">
      <circle cx="6" cy="6" r="2" />
      <circle cx="6" cy="18" r="2" />
      <circle cx="18" cy="12" r="2" />
      <path d="M6 8v8M6 12c0-3 2-6 6-6h4M12 6v0" />
    </svg>
  ),
  copy: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
    </svg>
  ),
  message: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  bell: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </svg>
  ),
  kanban: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <rect x="3" y="4" width="5" height="16" rx="1" />
      <rect x="10" y="4" width="5" height="10" rx="1" />
      <rect x="17" y="4" width="4" height="13" rx="1" />
    </svg>
  ),
  globe: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
  ),
  chart: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <path d="M3 21h18M6 17V9M11 17V5M16 17v-7M21 17v-4" />
    </svg>
  ),
  people: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2" />
      <path d="M3 19c0-3 3-5 6-5s6 2 6 5M14 19c0-2 2-3.5 4-3.5s4 1.5 4 3.5" />
    </svg>
  ),
  building: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <path d="M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16M16 8h2a2 2 0 0 1 2 2v11M4 21h16M8 7h2M8 11h2M8 15h2M8 19h2" />
    </svg>
  ),
  package: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <path d="M3 7l9-4 9 4-9 4-9-4z" />
      <path d="M3 7v10l9 4 9-4V7M12 11v10" />
    </svg>
  ),
  eye: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  link: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round">
      <path d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
    </svg>
  ),
  card: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18" />
    </svg>
  ),
  home: (p: IconProps = {}) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
      <path d="M3 11l9-8 9 8v9a2 2 0 0 1-2 2h-4v-7H9v7H5a2 2 0 0 1-2-2v-9z" />
    </svg>
  ),
} as const;

export type IconName = keyof typeof Icon;
